import { graphql } from "gatsby"
import React from "react"
import { Layout } from "@bw/layouts"
import { Seo, Section, Block, Socials, Spacer, Title, CmsImage } from "@bw/bits"
import { SimpleTeaser, Breadcrumbs, Delivery } from "@bw/partials"
import { PageHeader, Gallery, Employees } from "@bw/modules"
import { Content, Footer } from "../components/modules/mapModules.jsx"
import * as Icons from "@bw/icons"
import styled from "styled-components"
import { useTranslation } from "react-i18next"
import { OpeningHours, WeekDays, OpenState } from "@phoenix344/opening-hours"
import { breakpoints } from "../theme"
import parse from "html-react-parser"

const PrestaGrid = styled.div`
  margin-top: 3em;
  display: grid;
  grid-gap: 25px;
  grid-template-columns: 1fr 1fr 1fr;

  @media (min-width: ${breakpoints.medium}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }

  @media (min-width: ${breakpoints.large}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  }

  @media (min-width: ${breakpoints.xlarge}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  }
`

const ScheduleDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: bold;

  > span {
    margin: 0;
    font-weight: 300;
    font-size: 15px;
  }
`

const Logo = styled.div`
  @media (max-width: ${breakpoints.medium}px) {
    grid-row: 1 / 2;
  }
`

const Text = styled.div`
  font-weight: 300;
  font-size: 14px;
  line-height: 176.3%;
  margin-top: -3px;

  > a {
    color: var(--font-color);

    &[href^="tel"] {
      @media (min-width: ${breakpoints.medium}px) {
        text-decoration: none;
      }
    }
  }
`

const TextOpenings = styled(Text)`
  white-space: pre-wrap;
  b {
    width: 1.5em;
    display: inline-block;
  }
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  grid-gap: 25px;
  width: 100%;
  justify-content: space-between;

  .title {
    margin: 0px;
  }
`
const Infos = styled.div`
  display: grid;
  grid-gap: 15px;
  margin: 64px 0;

  @media (min-width: ${breakpoints.medium}px) {
    grid-template-columns: 1fr 125px;
  }
`

const Itinerary = styled.a`
  display: flex;
  align-items: center;
  margin-top: 7px;
  gap: 7px;
  font-weight: 700;
  font-size: 15px;
  text-decoration: none;
  line-height: 176.3%;
`

const StyledGrid = styled.div`
  display: grid;
  grid-gap: 15px;
  grid-template-columns: 1fr;

  @media (min-width: ${breakpoints.medium}px) {
    grid-template-columns: 1fr 1fr;
    grid-gap: 30px;
  }
`

const OpeningIndicator = ({ opened }) => {
  const { t } = useTranslation()
  const now = React.useMemo(() => new Date(), [opened])
  const isClosed = opened.getState(now) === OpenState.Closed

  return (
    <ScheduleDiv>
      {isClosed ? t("pharmacy.closed") : t("pharmacy.open")}
      {opened.isOpenSoon() === true && <span>{t("pharmacy.openSoon")}</span>}
      {opened.isClosedSoon() === true && (
        <span>{t("pharmacy.closedSoon")}</span>
      )}
    </ScheduleDiv>
  )
}

const PharmacyTemplate = ({ data, pageContext, location }) => {
  const pharmacy = data.collectionPharmacies
  const mount = data.mount
  const { t } = useTranslation()
  const url = "https://www.google.ch/maps/dir//pharmacieplus"
  const opened = React.useMemo(() => {
    const opened = new OpeningHours({
      // Predefine a date makes only sense to test the current day
      //currentDate: new Date(2020, 8, 15),
      text: {
        weekDays: [
          "<b>Di</b>",
          "<b>Lu</b>",
          "<b>Ma</b>",
          "<b>Me</b>",
          "<b>Je</b>",
          "<b>Ve</b>",
          "<b>Sa</b>",
        ],
        closed: "fermé",
        timespanSeparator: " - ",
      },
    })

    ;[
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ].forEach(day => {
      if (typeof pharmacy[day.toLowerCase()] === "undefined") {
        return false
      }

      pharmacy[day.toLowerCase()].forEach(hours => {
        if (!hours.opening || !hours.closing) {
          return false
        }
        opened.add(WeekDays[day], hours.opening, hours.closing)
      })
    })
    return opened
  }, [pharmacy])

  const geolocalization = encodeURIComponent(
    `${pharmacy.title}, ${pharmacy.street}, ${pharmacy.zipCode} ${pharmacy.city}`
  )
  const breadcrumbsLinks = [
    {
      title: "Accueil",
      to: "/",
    },
    {
      title: "Nos pharmacies",
      to: mount.slug,
    },
    {
      title: pharmacy.title,
      to: pharmacy.slug,
    },
  ]

  const mountImage = mount.header[0]?.image

  const content = <Content modules={pharmacy.content} />

  return (
    <Layout
      {...{
        pageContext,
        orderUrl: pharmacy.abilisLink,
        bookingUrl: pharmacy.onlinBookingLink,
      }}
      color={pharmacy.color?.value}
    >
      <Seo
        url={location.href}
        title={pharmacy.metaTitle || pharmacy.title}
        description={pharmacy.metaDescription}
        image={pharmacy.image?.url}
        meta={[
          {
            name: "robots",
            content: pharmacy.doNotIndex
              ? "noindex, nofollow"
              : "max-image-preview:large, index, follow",
          },
        ]}
      />
      <PageHeader
        title={pharmacy.title}
        pictogram={
          <svg className="icon">
            <use xlinkHref="/sprite-v2.svg#cross" />
          </svg>
        }
        subtitle={<OpeningIndicator opened={opened} />}
        color={pharmacy.color?.value}
        image={
          mountImage ? (
            <CmsImage src={mountImage} alt={mountImage.alt} />
          ) : undefined
        }
      />
      {breadcrumbsLinks && <Breadcrumbs breadcrumbs={breadcrumbsLinks} />}
      <Section containerSize="small">
        {pharmacy.images && <Gallery images={pharmacy.images} />}
        <Infos>
          <Spacer>
            {pharmacy.infoTitle && (
              <Block
                blockSuptitle={pharmacy.infoSuptitle}
                blockTitle={pharmacy.infoTitle}
                type="commander"
                titleSize="h4"
                important
              >
                <div style={{ marginTop: "25px", fontWeight: "300" }}>
                  {pharmacy.text ? parse(pharmacy.text) : undefined}
                </div>
              </Block>
            )}
          </Spacer>
          {pharmacy.logo && (
            <Logo>
              <img
                src={pharmacy.logo.url}
                alt={`Logo de la pharmacie ${pharmacy.title}`}
                height="110"
                width="auto"
              />
            </Logo>
          )}
        </Infos>
        <StyledGrid>
          <Spacer>
            <InfoGrid>
              <Title size="h4" level="div" title={t("pharmacy.phone")} />
              <Text>
                {pharmacy.phone ? (
                  <a href={`tel:${pharmacy.phone.replaceAll(" ", "")}`}>
                    {pharmacy.phone}
                  </a>
                ) : (
                  "–"
                )}
              </Text>
            </InfoGrid>
            <InfoGrid>
              <Title size="h4" level="div" title={t("pharmacy.address")} />
              <Text>
                {pharmacy.street}
                <br />
                {pharmacy.zipCode}&nbsp;
                {pharmacy.city}
                <Itinerary
                  href={url + geolocalization}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icons.ArrowRight />
                  {t("itinerary")}
                </Itinerary>
              </Text>
            </InfoGrid>
            {pharmacy.networks.length > 0 && (
              <InfoGrid>
                <div />
                <div>
                  <Socials networks={pharmacy.networks} />
                </div>
              </InfoGrid>
            )}
          </Spacer>
          <Spacer>
            <InfoGrid>
              <Title size="h4" level="div" title={t("pharmacy.email")} />
              <Text>
                {pharmacy.email ? (
                  <a href={`mailto:${pharmacy.email}`}>{pharmacy.email}</a>
                ) : (
                  "–"
                )}
              </Text>
            </InfoGrid>
            {pharmacy.website && (
              <InfoGrid>
                <Title size="h4" level="div" title={t("pharmacy.website")} />
                <Text>
                  <a href={pharmacy.website} target="_blank" rel="noreferrer">
                    {new URL(pharmacy.website).hostname?.replace("www.", "")}
                  </a>
                </Text>
              </InfoGrid>
            )}
            <InfoGrid>
              <Title size="h4" level="div" title={t("pharmacy.horaires")} />
              <TextOpenings>
                <OpeningIndicator opened={opened} />
                {parse(
                  opened.toString({ locales: "fr-FR" }).replace(/\[|\]/g, "")
                )}
                {pharmacy.specialSchedules
                  ? parse(pharmacy.specialSchedules)
                  : null}
              </TextOpenings>
            </InfoGrid>
          </Spacer>
        </StyledGrid>
        {pharmacy.deliveryConditions && (
          <Delivery title={pharmacy.deliveryConditions} />
        )}
      </Section>
      {pharmacy.prestations && pharmacy.prestations.length > 0 && (
        <Section titleIsInline={false} suptitle={t("pharmacy.teasersSuptitle")}>
          <PrestaGrid>
            {pharmacy.prestations.map((item, i) => (
              <SimpleTeaser
                key={i}
                title={item.title}
                excerpt={item.teaserExcerpt}
                to={item.slug}
                image={
                  item.teaserImage ? (
                    <CmsImage src={item.teaserImage} alt="" />
                  ) : null
                }
              />
            ))}
          </PrestaGrid>
        </Section>
      )}
      {content}
      {pharmacy.staff && pharmacy.staff.length > 0 && (
        <Section
          backgroundColor="var(--page-color-rgba)"
          suptitle="Une équipe"
          title="à votre service"
        >
          <Employees
            contents={pharmacy.staff.map(item => {
              return {
                firstName:
                  item.firstName === item.lastName ? undefined : item.firstName,
                lastName: item.lastName,
                role: item.function,
                portrait: item.portrait ? (
                  <CmsImage
                    src={item.portrait}
                    alt={`${item.firstName} ${item.lastName}`}
                  />
                ) : null,
              }
            })}
          />
        </Section>
      )}
      <Footer modules={pharmacy.footer} />
    </Layout>
  )
}

export default PharmacyTemplate

export const pharmacyQuery = graphql`
  query pharmacyQuery($id: String!) {
    collectionPharmacies(id: { eq: $id }) {
      title
      metaTitle
      metaDescription
      image {
        url
      }
      doNotIndex
      zipCode
      website
      street
      slug
      city
      color {
        value
      }
      abilisLink
      email
      latitude
      longitude
      onlinBookingLink
      deliveryConditions
      infoSuptitle
      infoTitle
      text
      logo {
        url
      }
      images {
        alt
        has_focus
        focus_css
        localImage {
          childImageSharp {
            gatsbyImageData(width: 600)
            original {
              width
              height
              src
            }
          }
        }
      }
      prestations {
        title
        teaserExcerpt
        slug
        teaserImage {
          localImage {
            childImageSharp {
              gatsbyImageData(width: 350, height: 175)
            }
          }
        }
      }
      staff {
        firstName
        lastName
        function
        portrait {
          localImage {
            childImageSharp {
              gatsbyImageData(width: 144)
            }
          }
        }
      }
      phone
      monday {
        opening
        closing
      }
      tuesday {
        opening
        closing
      }
      wednesday {
        opening
        closing
      }
      thursday {
        opening
        closing
      }
      saturday {
        opening
        closing
      }
      sunday {
        opening
        closing
      }
      friday {
        opening
        closing
      }
      specialSchedules
      id
      content {
        __typename
        suptitle
        title
        subtitle
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
        ancre

        ... on CallToActionModule {
          ...CallToActionModuleFragment
        }
        ... on ContentModule {
          ...ContentModuleFragment
        }
        ... on ContentWithImageModule {
          ...ContentWithImageModuleFragment
        }
        ... on GalleryModule {
          ...GalleryModuleFragment
        }
        ... on ContentOnColumnsModule {
          ...ContentOnColumnsModuleFragment
        }
        ... on TeasersLatestModule {
          ...TeasersLatestModuleFragment
        }
        ... on TeasersCustomModule {
          ...TeasersCustomModuleFragment
        }
        ... on TeasersManualModule {
          ...TeasersManualModuleFragment
        }
        ... on LogoGridModule {
          ...LogoGridModuleFragment
        }
        ... on ImageModule {
          ...ImageModuleFragment
        }
        ... on VideoModule {
          ...VideoModuleFragment
        }
        ... on FormModule {
          ...FormModuleFragment
        }
        ... on DownloadsModule {
          ...DownloadsModuleFragment
        }
      }
      footer {
        __typename
        suptitle
        title
        subtitle
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
      }
      networks {
        networkType {
          value
        }
        networkIcon {
          url
        }
        networkUrl
      }
    }
    mount: collectionPages(mount: { handle: { eq: "pharmacies" } }) {
      slug
      header {
        ... on PageHeaderModule {
          image {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                original {
                  src
                  width
                  height
                }
                gatsbyImageData(width: 1670, quality: 80)
              }
            }
          }
        }
      }
    }
  }
`
